"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AnimatedCircuitChipBackground.module.css";

/**
 * AnimatedCircuitChipBackground (Fast & Sequential Edition)
 * - Tiny Nodes (Small circles)
 * - High Speed
 * - Sequential Processing (One finishes, Next starts)
 * - Long continuous trails
 */
export default function AnimatedCircuitChipBackground({
  className = "",
  style,
  density = 20, 
  glow = 1.0,
  color = "var(--acb-color, #37C7FF)",
  secondary = "var(--acb-secondary, #1e3a8a)",
  traceOpacity = "var(--acb-trace-opacity, 0.15)",
  // پارامترهای جدید برای کنترل سرعت و اندازه:
  baseSpeed = 0.1, // سرعت پایه (خیلی سریعتر)
  nodeSize = 0.25, // اندازه دایره‌ها (خیلی کوچکتر)
}) {
  const id = useMemo(() => `acb-${Math.random().toString(16).slice(2)}`, []);
  const [pulses, setPulses] = useState([]);
  const rafRef = useRef(0);
  const lastRef = useRef(0);

  // -----------------------------------------------------------
  // 1. تولید هندسه (بدون تغییر نسبت به قبل، فقط برای محاسبه مسیرها)
  // -----------------------------------------------------------
  const { paths, nodes } = useMemo(() => {
    const rand = mulberry32(hashString(id));
    const paths = [];
    const nodes = [];

    const createBusBundle = (idx) => {
        const axis = rand() > 0.5 ? 0 : 1; 
        const laneCount = 2 + Math.floor(rand() * 2); 
        const spacing = 2.0; 
        
        let startX = 5 + rand() * 80;
        let startY = 5 + rand() * 80;
        const straightLen = 20 + rand() * 40;
        const hasTurn = rand() > 0.4; 

        for (let i = 0; i < laneCount; i++) {
            let sx = startX, sy = startY;
            if (axis === 0) sy += i * spacing;
            else sx += i * spacing;

            let d = "";
            let ex = sx, ey = sy;
            let totalLen = 0;

            if (!hasTurn) {
                if (axis === 0) ex += straightLen;
                else ey += straightLen;
                d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${ex.toFixed(1)} ${ey.toFixed(1)}`;
                totalLen = straightLen;
            } else {
                const leg1 = straightLen * (0.3 + rand() * 0.4);
                const leg2 = (rand() > 0.5 ? 15 : -15) + (rand() * 10);
                if (axis === 0) {
                    d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${(sx + leg1).toFixed(1)} ${sy.toFixed(1)} L ${(sx + leg1).toFixed(1)} ${(sy + leg2).toFixed(1)}`;
                    ex = sx + leg1;
                    ey = sy + leg2;
                } else {
                    d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${sx.toFixed(1)} ${(sy + leg1).toFixed(1)} L ${(sx + leg2).toFixed(1)} ${(sy + leg1).toFixed(1)}`;
                    ex = sx + leg2;
                    ey = sy + leg1;
                }
                totalLen = leg1 + Math.abs(leg2);
            }

            const pathId = `p-${idx}-${i}`;
            paths.push({ id: pathId, d: d, len: totalLen });

            nodes.push({ id: `n-${idx}-${i}-start`, pathId: pathId, type: 'start', x: sx, y: sy });
            nodes.push({ id: `n-${idx}-${i}-end`, pathId: pathId, type: 'end', x: ex, y: ey });
        }
    };

    const groups = Math.max(4, Math.floor(density / 2.5));
    for(let g=0; g<groups; g++) {
        createBusBundle(g);
    }

    return { paths, nodes };
  }, [density, id]);

  // -----------------------------------------------------------
  // 2. حلقه انیمیشن (Sequential Logic)
  // -----------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    function tick(ts) {
      if (!mounted) return;
      const last = lastRef.current || ts;
      const dt = Math.min(0.05, (ts - last) / 1000); // Delta Time
      lastRef.current = ts;

      setPulses((prev) => {
        let next = [];
        
        // الف) حرکت دادن پالس‌های موجود
        if (prev.length > 0) {
            next = prev
                .map((p) => ({ ...p, t: p.t + p.speed * dt }))
                // فقط وقتی نگهش دار که هنوز کامل محو نشده (کمی بیشتر از 1)
                // این باعث میشه نود انتهایی فرصت روشن شدن داشته باشه
                .filter((p) => p.t < 1.3); 
        }

        // ب) اگر هیچی تو صفحه نیست (قبلی تموم شده)، یکی جدید بساز
        if (next.length === 0 && paths.length > 0) {
            const pick = paths[Math.floor(Math.random() * paths.length)];
            next.push({
                id: `${Date.now()}-${Math.random()}`,
                pathId: pick.id,
                t: 0,
                // سرعت بالا و کمی متغیر
                speed: baseSpeed + Math.random() * 0.5, 
                len: pick.len
            });
        }

        return next;
      });

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [paths, baseSpeed]);

  // Map برای دسترسی سریع به پالس فعال
  const activePulsesMap = useMemo(() => {
      const map = new Map();
      pulses.forEach(p => map.set(p.pathId, p));
      return map;
  }, [pulses]);

  return (
    <svg
      className={`${styles.root} ${className}`}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%", background: "transparent", ...style }}
      aria-hidden="true"
    >
      <defs>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={1.0 * glow} result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* 1. مسیرهای خاموش */}
      <g opacity={traceOpacity}>
        {paths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="none"
            stroke={secondary}
            strokeWidth={0.3} // خطوط خاموش نازک‌تر
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
      
      {/* 2. نودهای خاموش (ریز) */}
      <g opacity={traceOpacity * 1.5}>
         {nodes.map((n) => (
             <circle key={n.id} cx={n.x} cy={n.y} r={nodeSize} fill={secondary} />
         ))}
      </g>

      {/* 3. پالس متحرک (جریان سریع و یکپارچه) */}
      <g filter={`url(#${id}-glow)`}>
        {pulses.map((pulse) => {
            const path = paths.find(p => p.id === pulse.pathId);
            if (!path) return null;
            
            // طول دم بسیار بلندتر برای حس سرعت و یکپارچگی
            const tailLen = 40; 
            const totalLen = path.len || 100;
            const currentPos = pulse.t * (totalLen + tailLen); 

            // محو شدن سریع در پایان
            const opacity = pulse.t > 1.0 ? Math.max(0, 1 - (pulse.t - 1.0) * 4) : 1;

            return (
                <path
                    key={pulse.id}
                    d={path.d}
                    fill="none"
                    stroke={color}
                    strokeWidth={0.8} // ضخامت نور
                    strokeLinecap="round" // سر گرد برای نرمی حرکت
                    // دش ارری: [طول خط روشن، فاصله خالی]
                    strokeDasharray={`${tailLen} ${totalLen + tailLen + 100}`}
                    strokeDashoffset={tailLen - currentPos + totalLen}
                    opacity={opacity}
                    vectorEffect="non-scaling-stroke"
                />
            );
        })}
      </g>

      {/* 4. نودهای فعال (چشمک زن ریز) */}
      <g filter={`url(#${id}-glow)`}>
          {nodes.map(node => {
              const pulse = activePulsesMap.get(node.pathId);
              if (!pulse) return null;

              let intensity = 0;
              
              // تنظیم زمان روشن شدن با سرعت جدید
              // چون سرعت بالاست، بازه‌های زمانی (t) باید کوتاه‌تر چک شوند
              if (node.type === 'start') {
                  if (pulse.t >= 0 && pulse.t < 0.2) {
                      intensity = 1 - (pulse.t / 0.2); 
                  }
              } else if (node.type === 'end') {
                  if (pulse.t > 0.9 && pulse.t < 1.3) {
                      // وقتی رسید ته خط، یک لحظه روشن شود
                      const progress = (pulse.t - 0.9) / 0.4;
                      intensity = Math.sin(progress * Math.PI); 
                      if(intensity < 0) intensity = 0;
                  }
              }

              if (intensity <= 0.01) return null;

              return (
                  <circle 
                    key={`active-${node.id}`}
                    cx={node.x}
                    cy={node.y}
                    // شعاع موقع روشن شدن کمی بیشتر از حالت عادی
                    r={nodeSize + intensity * 0.4} 
                    fill={color}
                    opacity={intensity}
                  />
              )
          })}
      </g>
    </svg>
  );
}

// ----------------- Helper Functions & Export -----------------

export function CircuitBackgroundLayer({ className = "", style }) {
  return (
    <AnimatedCircuitChipBackground
      className={`${styles.layer} ${className}`}
      density={28} // تراکم خطوط
      baseSpeed={2.5} // سرعت بسیار بالا
      nodeSize={0.25} // اندازه نقطه بسیار ریز
      glow={1.2}
      style={style}
    />
  );
}

function hashString(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

function mulberry32(seedFn) {
  let a = seedFn();
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}